import React, {useEffect, useState} from "react";
import {pageAuthorizedByGroup} from "../../fragments/PageAuthorization";
import {Link, useParams, useSearchParams} from "react-router-dom";
import {
	Box,
	Breadcrumbs,
	CircularProgress,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	Tab,
	Tabs,
	TextField,
	Typography
} from "@mui/material";
import {PentaSplit} from "../../../components/common/PentaSplit";
import {GET_ALL_CUSTOM_ATTRIBUTES_SETUP, PRODUCT_SUPPLIER_DETAILS, PUT_PRODUCT_SUPPLIER} from "../Queries";
import {graphQLServices} from "../../../services/GraphQLServices";
import {useQuery} from "@apollo/client";
import {TabPanel} from "../../../components/common/TabPanel";
import {ProductSupplier} from "../models/ProductSupplier";
import {ProOrCon} from "../models/ProOrCon";
import {Add, OpenInNew, Remove, Report} from "@mui/icons-material";
import {ProcessButton} from "../../../components/common/input/ProcessButton";
import {cloneMutationData} from "../Utils";
import {productsEditorDefaultTargetStage, productsEditorTargetStage} from "../ProductModuleOverview";
import {SustainabilityScore} from "../models/SustainabilityScore";
import {Feature} from "../models/Feature";
import {TaskDivision} from "../models/TaskDivision";
import {HowItWorks} from "../models/HowItWorks";
import {ProductsOverviewContent} from "../products-details/ProductsOverview";
import {SellingPoint} from "../models/SellingPoint";
import {
	breedBandWinkelProviderKrn, budgetThuisProviderKrn, caiwayProviderKrn, deltaProviderKrn, kpnProviderKrn,
	moneyViewProviderKrn, odidoProviderKrn, onlineNlProviderKrn,
	orderTypes,
	productCategories,
	riskProviderKrn, rollsProviderKrn, salesdockProviderKrn,
	supplierScoreSources,
	supplierStatuses,
	tenants, youfoneProviderKrn, ziggoProviderKrn,
	zorgKaartProviderKrn,
	zorgWebProviderKrn,
	achmeaProviderKrn,
	insharedProviderKrn
} from "../Data";
import {generateIndexObject, openNewTab} from "../../../libraries/util/Functions";
import {localStorageOfObject} from "../../../libraries/util/LocalStorage";
import {translateFunction} from "../../../localization/TranslationService";
import {HeaderSlot, StageSwitch} from "../../../components/onsite/Footer";
import {GlobalState} from "../../../global/GlobalState";
import {targetStageSafetyCheck} from "../../../libraries/util/SafetyCheck";
import {CustomAttributeSetup} from "../models/CustomAttributeSetup";
import {CustomAttribute} from "../models/CustomAttribute";

const translate = translateFunction("SureBasePortalProductsCatalog");

const tabs: string[] = [
	"info",
	"ga",
	"usps",
	"pros",
	"features",
	"task",
	"work",
	"products",
	"custom-attributes"
];

const tabsIndex = generateIndexObject(tabs);

const defaultTenantName = "overstappen";

const newSupplier = localStorageOfObject("newSupplier");

export const SuppliersDetails = pageAuthorizedByGroup(function () {
	const id = useParams()?.supplierId;
	const tabName = useParams()?.tab;
	const [params] = useSearchParams();
	
	const fullKrn = `krn:kizi:product-supplier:${id}`;
	const basePath = `/products/suppliers/${id}`;
	
	const targetStage = GlobalState.targetKiziStage(productsEditorDefaultTargetStage);
	const disabled = targetStage !== productsEditorTargetStage;
	const {loading, data, error} = useQuery(PRODUCT_SUPPLIER_DETAILS, {
		client: graphQLServices.productsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage: targetStage,
			krn: `krn:kizi:product-supplier:` + id,
		}
	});
	
	const customAttributesQuery = useQuery(GET_ALL_CUSTOM_ATTRIBUTES_SETUP, {
		client: graphQLServices.productsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage: productsEditorTargetStage,
		}
	});
	
	const [tab, setTab] = useState<number>(tabName ? tabsIndex[tabName] ?? 0 : 0);
	const tenant = useState(() => params.get("tenant"));
	const [supplier, setSupplier] = useState<ProductSupplier>();
	const [availableCustomAttributes, setAvailableCustomAttributes] = useState<CustomAttributeSetup[]>();
	// reset the state when stage changes
	// todo change this to proper effect that sets the state instead of clears the state, after removing the newProduct feature
	useEffect(() => {
		setSupplier(undefined);
	}, [targetStage]);
	
	if (loading || customAttributesQuery.loading)
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	
	if (error || customAttributesQuery.error)
		throw error;
	
	if (!supplier) {
		const newSupplierValue = newSupplier.get();
		if (newSupplierValue && newSupplierValue.krn === fullKrn) {
			setSupplier(newSupplierValue);
		} else {
			const nodes = data.productSuppliers.nodes;
			if (nodes.length === 0)
				throw new Error(`Product Supplier with krn ${fullKrn} is not found.`);
			const node: ProductSupplier = nodes[0];
			setSupplier(node);
		}
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	}
	
	
	if (!availableCustomAttributes) {
		const attributeNodes = customAttributesQuery.data.customAttributeSetup.nodes;
		if (attributeNodes === null || attributeNodes?.length === 0)
			throw new Error(`CustomAttributeSetups not found.`);
		
		setAvailableCustomAttributes(attributeNodes);
	}
	
	async function saveChanges(supplier: ProductSupplier) {
		targetStageSafetyCheck(targetStage);
		
		await graphQLServices.productsClient.mutate({
			mutation: PUT_PRODUCT_SUPPLIER,
			variables: {
				stage: targetStage,
				productSupplier: cloneMutationData(supplier),
			}
		});
		newSupplier.set(null);
	}
	
	function computeQuery(data: { tenant?: string } = {}) {
		const _tenant = data.tenant ?? tenant[0];
		const queryTenant = _tenant !== null ? "tenant=" + encodeURIComponent(_tenant) : undefined;
		const queryParams = [queryTenant].filter(it => !!it);
		return queryParams.length > 0 ? `?${queryParams.join("&")}` : "";
	}
	
	function updateUrl(data: { tab?: number; tenant?: string } = {}) {
		const _tab = data.tab ?? tab;
		const tabName = _tab > 0 ? "/" + tabs[_tab] : "";
		
		const query = computeQuery(data);
		window.history.replaceState(null, "", `${basePath}${tabName}${query}`);
	}
	
	function setTenant(newTenant: string) {
		updateUrl({tenant: newTenant});
		tenant[1](newTenant);
	}
	
	const props = {
		tab,
		supplier,
		tenant: [tenant[0], setTenant] as [string | null, (tenant: string | null) => void],
		setSupplier,
		disabled,
		availableCustomAttributes: availableCustomAttributes ?? []
	};
	
	return <>
		<HeaderSlot><StageSwitch defaultTargetStage={productsEditorDefaultTargetStage}/></HeaderSlot>
		<PentaSplit
			top={<Breadcrumbs style={{margin: "5px"}}>
				<Link style={{color: "inherit", textDecoration: "unset"}} to="/products">{translate("Products")}</Link>
				<Link style={{color: "inherit", textDecoration: "unset"}}
					to="/products/suppliers">{translate("Suppliers")}</Link>
				<Typography color="text.primary">{id}</Typography>
			</Breadcrumbs>}
			center={<Paper style={{
				height: "100%",
			}}>
				<Stack style={{height: "100%"}}>
					<div style={{flexGrow: 0}}>
						<Box sx={{borderBottom: 1, borderColor: "divider"}}>
							<Tabs value={tab} onChange={(event, newTab) => setTab(newTab)}>
								<Tab label={translate("ProductSupplier Information")} onClick={() => updateUrl({tab: 0})}/>
								<Tab label={translate("USP’s and Warnings")} onClick={() => updateUrl({tab: 1})}/>
								<Tab label={translate("Pros and Cons")} onClick={() => updateUrl({tab: 2})}/>
								<Tab label={translate("Features")} onClick={() => updateUrl({tab: 3})}/>
								<Tab label={translate("Task division")} onClick={() => updateUrl({tab: 4})}/>
								<Tab label={translate("How it works")} onClick={() => updateUrl({tab: 5})}/>
								<Tab label={translate("Products")} onClick={() => updateUrl({tab: 6})}/>
								<Tab label={translate("Custom attributes")} onClick={() => updateUrl({tab: 7})}/>
							</Tabs>
						</Box>
					</div>
					<div style={{flexGrow: 1, overflow: "hidden"}}>
						<Information {...props}/>
						<Usps {...props}/>
						<ProsCons {...props}/>
						<Features {...props}/>
						<TaskDivisionPanel {...props}/>
						<HowItWorksPanel {...props}/>
						<ProductsPanel {...props}/>
						<CustomAttributes {...props}/>
					</div>
					<div style={{flexGrow: 0, padding: "10px"}}>
						<ProcessButton
							buttonProps={{disabled}}
							defaultText={translate("Save")}
							inProgressText={translate("Saving")}
							completedText={translate("Saved")}
							resetKeys={[supplier]}
							variant={"outlined"}
							onClick={() => saveChanges(supplier)}/>
					</div>
				</Stack>
			</Paper>}
		/>
	</>;
}, ["Admin", "Product"]);

interface TabProps {
	readonly tab: number;
	readonly supplier: ProductSupplier;
	readonly disabled?: boolean;
	readonly availableCustomAttributes: CustomAttributeSetup[];
}

interface TenantProps {
	readonly tenant: [string | null, (tenant: string) => void];
}

interface SupplierProps {
	readonly setSupplier: (supplier: ProductSupplier) => void;
}

interface InformationProps extends TabProps, SupplierProps {
}

function CustomAttributes(props: InformationProps) {
	const customAttributes = props.supplier.customAttributes ?? [];
	
	return <TabPanel value={props.tab} index={7}>
		<PentaSplit
			top={<Stack spacing={2}>
				<div>{translate("Custom attributes")}</div>
			</Stack>}
			center={<Stack spacing={2} style={{height: "100%", overflow: "auto"}}>
				{customAttributes.map((it, index) => {
					return <Stack key={index} spacing={2} direction={"row"} alignItems={"end"} padding={"10px 0"}>
						<IconButton
							color={"primary"}
							disabled={props.disabled}
							onClick={() => {
								const newList = [...customAttributes];
								newList.splice(index, 1);
								props.setSupplier({...props.supplier, customAttributes: newList});
							}}
						>
							<Remove/>
						</IconButton>
						<Select
							label={translate("Custom attributes")}
							variant={"standard"}
							style={{flexGrow: 1}}
							value={it.description ?? ""}
							onChange={event => {
								const newItem = {...it, description: event.target.value};
								const newList = [...customAttributes];
								newList[index] = newItem;
								props.setSupplier({...props.supplier, customAttributes: newList});
							}}
						>
							{it.description && <MenuItem key={it.description} value={it.description}>{it.description}</MenuItem>}
							{props.availableCustomAttributes?.filter((x) => {
								return !props.supplier.customAttributes?.find(y => y.description === x.description);
							})?.map(x =>
								<MenuItem key={x.description} value={x.description}>{x.description}</MenuItem>)}
						</Select>
						
						<TextField
							variant={"standard"}
							label={translate("Value")}
							value={it.value ?? ""}
							style={{flexGrow: 1}}
							disabled={props.disabled}
							onChange={event => {
								const newItem = {...it, value: event.target.value};
								const newList = [...customAttributes];
								newList[index] = newItem;
								props.setSupplier({...props.supplier, customAttributes: newList});
							}}
						/>
					</Stack>;
				}).filter(it => !!it)}
				
				{props.availableCustomAttributes.length !== props.supplier.customAttributes?.length && <div>
					<IconButton
						color={"primary"}
						disabled={props.disabled}
						onClick={() => {
							const newList = [...customAttributes, {} as CustomAttribute];
							props.setSupplier({...props.supplier, customAttributes: newList});
						}}
					>
						<Add/>
					</IconButton>
				</div>}
			</Stack>}
		/>
	</TabPanel>;
}

function Information(props: InformationProps) {
	const externalIds = props.supplier.externalIds ?? [];
	const sustainabilityScores = props.supplier.sustainabilityScore ?? [];
	
	return <TabPanel value={props.tab} index={0}>
		{/* todo make all fields editable for manager */}
		<div style={{height: "100%", overflow: "auto"}}>
			<Stack spacing={2}>
				<div>ProductSupplier Information</div>
				<TextField
					label={translate("Kizi Resource Name")}
					value={props.supplier.krn}
					variant={"filled"}
					disabled
				/>
				<TextField
					label={translate("Legal Name")}
					value={props.supplier.legalName ?? ""}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => props.setSupplier({...props.supplier, legalName: event.target.value})}
				/>
				<TextField
					label={translate("Name")}
					value={props.supplier.name}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => props.setSupplier({...props.supplier, name: event.target.value})}
				/>
				<FormControl>
					<InputLabel id={"status"}>{translate("Status")}</InputLabel>
					<Select
						labelId={"status"}
						label={translate("Status")}
						variant={"standard"}
						value={props.supplier.state ?? ""}
						disabled={props.disabled}
						onChange={event => props.setSupplier({...props.supplier, state: event.target.value})}
					>
						<MenuItem key={"null"} value={""} hidden={true} style={{display: "none"}}></MenuItem>
						{supplierStatuses.map(it =>
							<MenuItem key={it.toLowerCase()} value={it.toUpperCase()}>{translate(it)}</MenuItem>)}
					</Select>
				</FormControl>
				
				<TextField
					label={translate("Risk ID")}
					value={externalIds.find(it => it.providerKrn === riskProviderKrn)?.externalSupplierId ?? ""}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => {
						const supplierId = event.target.value;
						const newExternalIds = [...externalIds];
						const index = newExternalIds.findIndex(value => value.providerKrn === riskProviderKrn);
						if (supplierId) {
							if (index === -1) {
								newExternalIds.push({
									providerKrn: riskProviderKrn,
									externalSupplierId: event.target.value
								});
							} else {
								newExternalIds[index] = {
									providerKrn: riskProviderKrn,
									externalSupplierId: event.target.value
								};
							}
						} else if (index >= 0) {
							newExternalIds.splice(index, 1);
						}
						props.setSupplier({...props.supplier, externalIds: newExternalIds});
					}}
				/>
				<TextField
					label={translate("Moneyview ID")}
					value={externalIds.find(it => it.providerKrn === moneyViewProviderKrn)?.externalSupplierId ?? ""}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => {
						const supplierId = event.target.value;
						const newExternalIds = [...externalIds];
						const index = newExternalIds.findIndex(value => value.providerKrn === moneyViewProviderKrn);
						if (supplierId) {
							if (index === -1) {
								newExternalIds.push({
									providerKrn: moneyViewProviderKrn,
									externalSupplierId: event.target.value
								});
							} else {
								newExternalIds[index] = {
									providerKrn: moneyViewProviderKrn,
									externalSupplierId: event.target.value
								};
							}
						} else if (index >= 0) {
							newExternalIds.splice(index, 1);
						}
						props.setSupplier({...props.supplier, externalIds: newExternalIds});
					}}
				/>
				<TextField
					label={translate("Breedbandwinkel ID")}
					value={externalIds.find(it => it.providerKrn === breedBandWinkelProviderKrn)?.externalSupplierId ?? ""}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => {
						const supplierId = event.target.value;
						const newExternalIds = [...externalIds];
						const index = newExternalIds.findIndex(value => value.providerKrn === breedBandWinkelProviderKrn);
						if (supplierId) {
							if (index === -1) {
								newExternalIds.push({
									providerKrn: breedBandWinkelProviderKrn,
									externalSupplierId: event.target.value
								});
							} else {
								newExternalIds[index] = {
									providerKrn: breedBandWinkelProviderKrn,
									externalSupplierId: event.target.value
								};
							}
						} else if (index >= 0) {
							newExternalIds.splice(index, 1);
						}
						props.setSupplier({...props.supplier, externalIds: newExternalIds});
					}}
				/>
				<TextField
					label={translate("Zorgweb ID")}
					value={externalIds.find(it => it.providerKrn === zorgWebProviderKrn)?.externalSupplierId ?? ""}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => {
						const supplierId = event.target.value;
						const newExternalIds = [...externalIds];
						const index = newExternalIds.findIndex(value => value.providerKrn === zorgWebProviderKrn);
						if (supplierId) {
							if (index === -1) {
								newExternalIds.push({
									providerKrn: zorgWebProviderKrn,
									externalSupplierId: event.target.value
								});
							} else {
								newExternalIds[index] = {
									providerKrn: zorgWebProviderKrn,
									externalSupplierId: event.target.value
								};
							}
						} else if (index >= 0) {
							newExternalIds.splice(index, 1);
						}
						props.setSupplier({...props.supplier, externalIds: newExternalIds});
					}}
				/>
				<TextField
					label={translate("Salesdock ID")}
					value={externalIds.find(it => it.providerKrn === salesdockProviderKrn)?.externalSupplierId ?? ""}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => {
						const supplierId = event.target.value;
						const newExternalIds = [...externalIds];
						const index = newExternalIds.findIndex(value => value.providerKrn === salesdockProviderKrn);
						if (supplierId) {
							if (index === -1) {
								newExternalIds.push({
									providerKrn: salesdockProviderKrn,
									externalSupplierId: event.target.value
								});
							} else {
								newExternalIds[index] = {
									providerKrn: salesdockProviderKrn,
									externalSupplierId: event.target.value
								};
							}
						} else if (index >= 0) {
							newExternalIds.splice(index, 1);
						}
						props.setSupplier({...props.supplier, externalIds: newExternalIds});
					}}
				/>
				<TextField
					label={translate("Rolls ID")}
					value={externalIds.find(it => it.providerKrn === rollsProviderKrn)?.externalSupplierId ?? ""}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => {
						const supplierId = event.target.value;
						const newExternalIds = [...externalIds];
						const index = newExternalIds.findIndex(value => value.providerKrn === rollsProviderKrn);
						if (supplierId) {
							if (index === -1) {
								newExternalIds.push({
									providerKrn: rollsProviderKrn,
									externalSupplierId: event.target.value
								});
							} else {
								newExternalIds[index] = {
									providerKrn: rollsProviderKrn,
									externalSupplierId: event.target.value
								};
							}
						} else if (index >= 0) {
							newExternalIds.splice(index, 1);
						}
						props.setSupplier({...props.supplier, externalIds: newExternalIds});
					}}
				/>
				<TextField
					label={translate("Achmea ID")}
					value={externalIds.find(it => it.providerKrn === achmeaProviderKrn)?.externalSupplierId ?? ""}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => {
						const supplierId = event.target.value;
						const newExternalIds = [...externalIds];
						const index = newExternalIds.findIndex(value => value.providerKrn === achmeaProviderKrn);
						if (supplierId) {
							if (index === -1) {
								newExternalIds.push({
									providerKrn: achmeaProviderKrn,
									externalSupplierId: event.target.value
								});
							} else {
								newExternalIds[index] = {
									providerKrn: achmeaProviderKrn,
									externalSupplierId: event.target.value
								};
							}
						} else if (index >= 0) {
							newExternalIds.splice(index, 1);
						}
						props.setSupplier({...props.supplier, externalIds: newExternalIds});
					}}
				/>
				<TextField
					label={translate("Inshared ID")}
					value={externalIds.find(it => it.providerKrn === insharedProviderKrn)?.externalSupplierId ?? ""}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => {
						const supplierId = event.target.value;
						const newExternalIds = [...externalIds];
						const index = newExternalIds.findIndex(value => value.providerKrn === insharedProviderKrn);
						if (supplierId) {
							if (index === -1) {
								newExternalIds.push({
									providerKrn: insharedProviderKrn,
									externalSupplierId: event.target.value
								});
							} else {
								newExternalIds[index] = {
									providerKrn: insharedProviderKrn,
									externalSupplierId: event.target.value
								};
							}
						} else if (index >= 0) {
							newExternalIds.splice(index, 1);
						}
						props.setSupplier({...props.supplier, externalIds: newExternalIds});
					}}
				/>
				<TextField
					label={translate("ZorgKaart ID")}
					value={externalIds.find(it => it.providerKrn === zorgKaartProviderKrn)?.externalSupplierId ?? ""}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => {
						const supplierId = event.target.value;
						const newExternalIds = [...externalIds];
						const index = newExternalIds.findIndex(value => value.providerKrn === zorgKaartProviderKrn);
						if (supplierId) {
							if (index === -1) {
								newExternalIds.push({
									providerKrn: zorgKaartProviderKrn,
									externalSupplierId: event.target.value
								});
							} else {
								newExternalIds[index] = {
									providerKrn: zorgKaartProviderKrn,
									externalSupplierId: event.target.value
								};
							}
						} else if (index >= 0) {
							newExternalIds.splice(index, 1);
						}
						props.setSupplier({...props.supplier, externalIds: newExternalIds});
					}}
				/>
				<TextField
					label={translate("KPN ID")}
					value={externalIds.find(it => it.providerKrn === kpnProviderKrn)?.externalSupplierId ?? ""}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => {
						const supplierId = event.target.value;
						const newExternalIds = [...externalIds];
						const index = newExternalIds.findIndex(value => value.providerKrn === kpnProviderKrn);
						if (supplierId) {
							if (index === -1) {
								newExternalIds.push({
									providerKrn: kpnProviderKrn,
									externalSupplierId: event.target.value
								});
							} else {
								newExternalIds[index] = {
									providerKrn: kpnProviderKrn,
									externalSupplierId: event.target.value
								};
							}
						} else if (index >= 0) {
							newExternalIds.splice(index, 1);
						}
						props.setSupplier({...props.supplier, externalIds: newExternalIds});
					}}
				/>
				<TextField
					label={translate("BudgetThuis ID")}
					value={externalIds.find(it => it.providerKrn === budgetThuisProviderKrn)?.externalSupplierId ?? ""}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => {
						const supplierId = event.target.value;
						const newExternalIds = [...externalIds];
						const index = newExternalIds.findIndex(value => value.providerKrn === budgetThuisProviderKrn);
						if (supplierId) {
							if (index === -1) {
								newExternalIds.push({
									providerKrn: budgetThuisProviderKrn,
									externalSupplierId: event.target.value
								});
							} else {
								newExternalIds[index] = {
									providerKrn: budgetThuisProviderKrn,
									externalSupplierId: event.target.value
								};
							}
						} else if (index >= 0) {
							newExternalIds.splice(index, 1);
						}
						props.setSupplier({...props.supplier, externalIds: newExternalIds});
					}}
				/>
				<TextField
					label={translate("Delta ID")}
					value={externalIds.find(it => it.providerKrn === deltaProviderKrn)?.externalSupplierId ?? ""}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => {
						const supplierId = event.target.value;
						const newExternalIds = [...externalIds];
						const index = newExternalIds.findIndex(value => value.providerKrn === deltaProviderKrn);
						if (supplierId) {
							if (index === -1) {
								newExternalIds.push({
									providerKrn: deltaProviderKrn,
									externalSupplierId: event.target.value
								});
							} else {
								newExternalIds[index] = {
									providerKrn: deltaProviderKrn,
									externalSupplierId: event.target.value
								};
							}
						} else if (index >= 0) {
							newExternalIds.splice(index, 1);
						}
						props.setSupplier({...props.supplier, externalIds: newExternalIds});
					}}
				/>
				<TextField
					label={translate("Caiway ID")}
					value={externalIds.find(it => it.providerKrn === caiwayProviderKrn)?.externalSupplierId ?? ""}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => {
						const supplierId = event.target.value;
						const newExternalIds = [...externalIds];
						const index = newExternalIds.findIndex(value => value.providerKrn === caiwayProviderKrn);
						if (supplierId) {
							if (index === -1) {
								newExternalIds.push({
									providerKrn: caiwayProviderKrn,
									externalSupplierId: event.target.value
								});
							} else {
								newExternalIds[index] = {
									providerKrn: caiwayProviderKrn,
									externalSupplierId: event.target.value
								};
							}
						} else if (index >= 0) {
							newExternalIds.splice(index, 1);
						}
						props.setSupplier({...props.supplier, externalIds: newExternalIds});
					}}
				/>
				<TextField
					label={translate("Youfone ID")}
					value={externalIds.find(it => it.providerKrn === youfoneProviderKrn)?.externalSupplierId ?? ""}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => {
						const supplierId = event.target.value;
						const newExternalIds = [...externalIds];
						const index = newExternalIds.findIndex(value => value.providerKrn === youfoneProviderKrn);
						if (supplierId) {
							if (index === -1) {
								newExternalIds.push({
									providerKrn: youfoneProviderKrn,
									externalSupplierId: event.target.value
								});
							} else {
								newExternalIds[index] = {
									providerKrn: youfoneProviderKrn,
									externalSupplierId: event.target.value
								};
							}
						} else if (index >= 0) {
							newExternalIds.splice(index, 1);
						}
						props.setSupplier({...props.supplier, externalIds: newExternalIds});
					}}
				/>
				<TextField
					label={translate("Ziggo ID")}
					value={externalIds.find(it => it.providerKrn === ziggoProviderKrn)?.externalSupplierId ?? ""}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => {
						const supplierId = event.target.value;
						const newExternalIds = [...externalIds];
						const index = newExternalIds.findIndex(value => value.providerKrn === ziggoProviderKrn);
						if (supplierId) {
							if (index === -1) {
								newExternalIds.push({
									providerKrn: ziggoProviderKrn,
									externalSupplierId: event.target.value
								});
							} else {
								newExternalIds[index] = {
									providerKrn: ziggoProviderKrn,
									externalSupplierId: event.target.value
								};
							}
						} else if (index >= 0) {
							newExternalIds.splice(index, 1);
						}
						props.setSupplier({...props.supplier, externalIds: newExternalIds});
					}}
				/>
				<TextField
					label={translate("OnlineNl ID")}
					value={externalIds.find(it => it.providerKrn === onlineNlProviderKrn)?.externalSupplierId ?? ""}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => {
						const supplierId = event.target.value;
						const newExternalIds = [...externalIds];
						const index = newExternalIds.findIndex(value => value.providerKrn === onlineNlProviderKrn);
						if (supplierId) {
							if (index === -1) {
								newExternalIds.push({
									providerKrn: onlineNlProviderKrn,
									externalSupplierId: event.target.value
								});
							} else {
								newExternalIds[index] = {
									providerKrn: onlineNlProviderKrn,
									externalSupplierId: event.target.value
								};
							}
						} else if (index >= 0) {
							newExternalIds.splice(index, 1);
						}
						props.setSupplier({...props.supplier, externalIds: newExternalIds});
					}}
				/>
				<TextField
					label={translate("Odido ID")}
					value={externalIds.find(it => it.providerKrn === odidoProviderKrn)?.externalSupplierId ?? ""}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => {
						const supplierId = event.target.value;
						const newExternalIds = [...externalIds];
						const index = newExternalIds.findIndex(value => value.providerKrn === odidoProviderKrn);
						if (supplierId) {
							if (index === -1) {
								newExternalIds.push({
									providerKrn: odidoProviderKrn,
									externalSupplierId: event.target.value
								});
							} else {
								newExternalIds[index] = {
									providerKrn: odidoProviderKrn,
									externalSupplierId: event.target.value
								};
							}
						} else if (index >= 0) {
							newExternalIds.splice(index, 1);
						}
						props.setSupplier({...props.supplier, externalIds: newExternalIds});
					}}
				/>
				<TextField
					label={translate("Logo URL")}
					value={props.supplier.nameLogoUrl}
					variant={"standard"}
					disabled={props.disabled}
					InputProps={{
						startAdornment: <IconButton onClick={() => openNewTab(props.supplier.nameLogoUrl)}>
							<OpenInNew/>
						</IconButton>,
					}}
					onChange={event => props.setSupplier({...props.supplier, nameLogoUrl: event.target.value})}
				/>
				<div>
					<object key={props.supplier.nameLogoUrl} data={props.supplier.nameLogoUrl ?? ""} type="image/png">
						<Stack direction={"row"} padding={"20px"}>
							<Report color={"error"}/>
							<span>{translate("Image not found.")}</span>
						</Stack>
					</object>
				</div>
				<TextField
					label={translate("About")}
					value={props.supplier.about ?? ""}
					variant={"standard"}
					disabled={props.disabled}
					onChange={event => props.setSupplier({...props.supplier, about: event.target.value || null})}
					multiline
				/>
				<div>{translate("Scores")}</div>
				<Stack>
					{sustainabilityScores.map((it, index) => {
						return <Stack key={index} spacing={2} direction={"row"} alignItems={"end"} padding={"10px 0"}>
							<IconButton
								color={"primary"}
								disabled={props.disabled}
								onClick={() => {
									const newList = [...sustainabilityScores];
									newList.splice(index, 1);
									props.setSupplier({...props.supplier, sustainabilityScore: newList});
								}}
							>
								<Remove/>
							</IconButton>
							<FormControl>
								<InputLabel id={`${index}-score-source-label`}>{translate("Source")}</InputLabel>
								<Select
									style={{width: "200px"}}
									labelId={`${index}-score-source-label`}
									label={translate("Source")}
									variant={"standard"}
									value={it.source ?? ""}
									disabled={props.disabled}
									onChange={event => {
										const newItem: SustainabilityScore = {...it, source: event.target.value};
										const newList = [...sustainabilityScores];
										newList[index] = newItem;
										props.setSupplier({...props.supplier, sustainabilityScore: newList});
									}}
								>
									<MenuItem key={"null"} value={""} hidden={true}
										style={{display: "none"}}></MenuItem>
									{supplierScoreSources.map(it =>
										<MenuItem key={it.toLowerCase()}
											value={it.toLowerCase()}>{translate(it)}</MenuItem>)}
								</Select>
							</FormControl>
							<TextField
								variant={"standard"}
								label={translate("Score")}
								type={"number"}
								value={it.score ?? ""}
								style={{flexGrow: 1}}
								disabled={props.disabled}
								inputProps={{
									step: "0.1",
									lang: "nl-NL",
								}}
								onChange={event => {
									const numberValue = event.target.value ? Number(event.target.value) : undefined;
									// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
									const newItem: SustainabilityScore = {...it, score: numberValue!};
									const newList = [...sustainabilityScores];
									newList[index] = newItem;
									props.setSupplier({...props.supplier, sustainabilityScore: newList});
								}}
							/>
						</Stack>;
					})}
				</Stack>
				<div>
					<IconButton
						color={"primary"}
						disabled={props.disabled}
						onClick={() => {
							const newList = [...sustainabilityScores, {} as SustainabilityScore];
							props.setSupplier({...props.supplier, sustainabilityScore: newList});
						}}
					>
						<Add/>
					</IconButton>
				</div>
				{/* todo access */}
				{/*<TextField*/}
				{/*	label={"Access"}*/}
				{/*	value={""}*/}
				{/*	variant={"standard"}*/}
				{/*/>*/}
			</Stack>
		</div>
	</TabPanel>;
}

interface UspsProps extends TabProps, TenantProps, SupplierProps {
}

function Usps(props: UspsProps) {
	const tenantId = props.tenant[0] ?? defaultTenantName;
	
	const sellingPoints = props.supplier.sellingPoints ?? [];
	
	function count(tenant: string) {
		return sellingPoints.filter(it => it.clientName === tenant).length;
	}
	
	return <TabPanel value={props.tab} index={1}>
		<PentaSplit
			top={<Stack spacing={2}>
				<div>{translate("USP’s and Warnings")}</div>
				<FormControl>
					<InputLabel id={"tenant-label"}>{translate("Tenant")}</InputLabel>
					<Select
						labelId={"tenant-label"}
						label={translate("Tenant")}
						variant={"standard"}
						value={tenantId}
						onChange={it => props.tenant[1](it.target.value)}
					>
						{tenants.map(it =>
							<MenuItem key={it.toLowerCase()}
								value={it.toLowerCase()}>{translate(it)} ({count(it.toLowerCase())})</MenuItem>)}
					</Select>
				</FormControl>
			</Stack>}
			center={<Stack spacing={2} style={{height: "100%", overflow: "auto"}}>
				{sellingPoints.map((it, index) => {
					if (tenantId !== it.clientName)
						return undefined;
					return <Stack key={index} spacing={2} direction={"row"} alignItems={"end"} padding={"10px 0"}>
						<IconButton
							color={"primary"}
							disabled={props.disabled}
							onClick={() => {
								const newList = [...sellingPoints];
								newList.splice(index, 1);
								props.setSupplier({...props.supplier, sellingPoints: newList});
							}}
						>
							<Remove/>
						</IconButton>
						<FormControl>
							<InputLabel
								id={`${index}-product-category-label`}>{translate("Product Category")}</InputLabel>
							<Select
								style={{width: "200px"}}
								labelId={`${index}-product-category-label`}
								label={translate("Product Category")}
								variant={"standard"}
								value={it.productCategory ?? ""}
								disabled={props.disabled}
								onChange={event => {
									const newItem = {...it, productCategory: event.target.value};
									const newList = [...sellingPoints];
									newList[index] = newItem;
									props.setSupplier({...props.supplier, sellingPoints: newList});
								}}
							>
								<MenuItem key={"null"} value={""} hidden={true} style={{display: "none"}}></MenuItem>
								{productCategories.map(it =>
									<MenuItem key={it.toLowerCase()}
										value={it.toUpperCase()}>{translate(it)}</MenuItem>)}
							</Select>
						</FormControl>
						<FormControl>
							<InputLabel id={`${index}-order-type-label`}>{translate("Order Type")}</InputLabel>
							<Select
								style={{width: "200px"}}
								labelId={`${index}-order-type-label`}
								label={translate("Order Type")}
								variant={"standard"}
								value={it.orderType ?? ""}
								disabled={props.disabled}
								onChange={event => {
									const newItem = {...it, orderType: event.target.value};
									const newList = [...sellingPoints];
									newList[index] = newItem;
									props.setSupplier({...props.supplier, sellingPoints: newList});
								}}
							>
								<MenuItem key={"null"} value={""} hidden={true} style={{display: "none"}}></MenuItem>
								{orderTypes.map(it =>
									<MenuItem key={it.toLowerCase()}
										value={it.toUpperCase()}>{translate(it)}</MenuItem>)}
							</Select>
						</FormControl>
						<TextField
							variant={"standard"}
							label={translate("Description")}
							value={it.description ?? ""}
							style={{flexGrow: 1}}
							disabled={props.disabled}
							onChange={event => {
								const newItem = {...it, description: event.target.value};
								const newList = [...sellingPoints];
								newList[index] = newItem;
								props.setSupplier({...props.supplier, sellingPoints: newList});
							}}
						/>
						<TextField
							variant={"standard"}
							label={translate("Value")}
							value={it.value ?? ""}
							style={{flexGrow: 1}}
							disabled={props.disabled}
							onChange={event => {
								const newItem = {...it, value: event.target.value};
								const newList = [...sellingPoints];
								newList[index] = newItem;
								props.setSupplier({...props.supplier, sellingPoints: newList});
							}}
						/>
					</Stack>;
				}).filter(it => !!it)}
				
				<div>
					<IconButton
						color={"primary"}
						disabled={props.disabled}
						onClick={() => {
							const newList = [...sellingPoints, {
								clientName: tenantId,
								sellingPointType: "UNIQUE_SELLING_POINT"
							} as SellingPoint];
							props.setSupplier({...props.supplier, sellingPoints: newList});
						}}
					>
						<Add/>
					</IconButton>
				</div>
			</Stack>}
		/>
	</TabPanel>;
}

interface ProsConsProps extends TabProps, TenantProps, SupplierProps {
}

function ProsCons(props: ProsConsProps) {
	const tenantId = props.tenant[0] ?? defaultTenantName;
	
	function count(tenant: string) {
		return (props.supplier.advantages ?? []).filter(it => it.clientName === tenant).length
			+ (props.supplier.disadvantages ?? []).filter(it => it.clientName === tenant).length;
	}
	
	return <TabPanel value={props.tab} index={2}>
		<PentaSplit
			top={<Stack spacing={2}>
				<div>{translate("Pros and Cons")}</div>
				<FormControl>
					<InputLabel id={"tenant-label"}>{translate("Tenant")}</InputLabel>
					<Select
						labelId={"tenant-label"}
						label={translate("Tenant")}
						variant={"standard"}
						value={tenantId}
						onChange={it => props.tenant[1](it.target.value)}
					>
						{tenants.map(it =>
							<MenuItem key={it.toLowerCase()}
								value={it.toLowerCase()}>{translate(it)} ({count(it.toLowerCase())})</MenuItem>)}
					</Select>
				</FormControl>
			</Stack>}
			center={<Stack spacing={2} style={{height: "100%", overflow: "auto"}}>
				<ProsConsDetails title={translate("Pros")} tenant={tenantId ?? defaultTenantName}
					list={props.supplier.advantages ?? []}
					setList={it => props.setSupplier({...props.supplier, advantages: it})}
					disabled={props.disabled}
				/>
				<ProsConsDetails title={translate("Cons")} tenant={tenantId ?? defaultTenantName}
					list={props.supplier.disadvantages ?? []}
					setList={it => props.setSupplier({...props.supplier, disadvantages: it})}
					disabled={props.disabled}
				/>
			</Stack>}
		/>
	</TabPanel>;
}

function ProsConsDetails(props: { title: string; tenant: string; list: ProOrCon[]; setList: (list: ProOrCon[]) => void; disabled?: boolean }) {
	return <>
		<div><h4>{props.title}:</h4></div>
		
		<Stack>
			{props.list.map((it, index) => {
				if (props.tenant !== it.clientName)
					return undefined;
				return <Stack key={index} spacing={2} direction={"row"} alignItems={"end"} padding={"10px 0"}>
					<IconButton
						color={"primary"}
						disabled={props.disabled}
						onClick={() => {
							const newList = [...props.list];
							newList.splice(index, 1);
							props.setList(newList);
						}}
					>
						<Remove/>
					</IconButton>
					<FormControl>
						<InputLabel
							id={`${props.title}-${index}-product-category-label`}>{translate("Product Category")}</InputLabel>
						<Select
							style={{width: "200px"}}
							labelId={`${props.title}-${index}-product-category-label`}
							label={translate("Product Category")}
							variant={"standard"}
							value={it.productCategory ?? ""}
							disabled={props.disabled}
							onChange={event => {
								const newItem = {...it, productCategory: event.target.value};
								const newList = [...props.list];
								newList[index] = newItem;
								props.setList(newList);
							}}
						>
							<MenuItem key={"null"} value={""} hidden={true} style={{display: "none"}}></MenuItem>
							{productCategories.map(it =>
								<MenuItem key={it.toLowerCase()} value={it.toUpperCase()}>{translate(it)}</MenuItem>)}
						</Select>
					</FormControl>
					<TextField
						variant={"standard"}
						label={translate("Description")}
						value={it.description}
						style={{flexGrow: 1}}
						disabled={props.disabled}
						onChange={event => {
							const newItem = {...it, description: event.target.value};
							const newList = [...props.list];
							newList[index] = newItem;
							props.setList(newList);
						}}
					/>
				</Stack>;
			}).filter(it => !!it)}
			
			<div>
				<IconButton
					color={"primary"}
					disabled={props.disabled}
					onClick={() => {
						const newList = [...props.list, {clientName: props.tenant} as ProOrCon];
						props.setList(newList);
					}}
				>
					<Add/>
				</IconButton>
			</div>
		</Stack>
	</>;
}

interface FeaturesProps extends TabProps {
	readonly tenant: [string | null, (tenant: string | null) => void];
	readonly setSupplier: (supplier: ProductSupplier) => void;
}

function Features(props: FeaturesProps) {
	const tenantId = props.tenant[0];
	
	const features = props.supplier.features ?? [];
	
	function count(tenant: string | null) {
		return features.filter(it => it.clientName === tenant).length;
	}
	
	return <TabPanel value={props.tab} index={3}>
		<PentaSplit
			top={<Stack spacing={2}>
				<div>{translate("Features")}</div>
				<FormControl>
					<InputLabel id={"tenant-label"}>{translate("Tenant")}</InputLabel>
					<Select
						labelId={"tenant-label"}
						label={translate("Tenant")}
						variant={"standard"}
						value={tenantId ?? "null"}
						onChange={it => props.tenant[1](it.target.value === "null" ? null : it.target.value)}
					>
						<MenuItem key={"all"}
							value={"null"}>{translate("All")} ({count(null)})</MenuItem>)
						{tenants.map(it =>
							<MenuItem key={it.toLowerCase()}
								value={it.toLowerCase()}>{translate(it)} ({count(it.toLowerCase())})</MenuItem>)}
					</Select>
				</FormControl>
			</Stack>}
			center={<Stack spacing={2} style={{height: "100%", overflow: "auto"}}>
				{features.map((it, index) => {
					if (tenantId !== it.clientName)
						return undefined;
					return <Stack key={index} spacing={2} direction={"row"} alignItems={"end"} padding={"10px 0"}>
						<IconButton
							color={"primary"}
							disabled={props.disabled}
							onClick={() => {
								const newList = [...features];
								newList.splice(index, 1);
								props.setSupplier({...props.supplier, features: newList});
							}}
						>
							<Remove/>
						</IconButton>
						<FormControl>
							<InputLabel
								id={`${index}-product-category-label`}>{translate("Product Category")}</InputLabel>
							<Select
								style={{width: "200px"}}
								labelId={`${index}-product-category-label`}
								label={translate("Product Category")}
								variant={"standard"}
								value={it.productCategory ?? ""}
								disabled={props.disabled}
								onChange={event => {
									const newItem = {...it, productCategory: event.target.value};
									const newList = [...features];
									newList[index] = newItem;
									props.setSupplier({...props.supplier, features: newList});
								}}
							>
								<MenuItem key={"null"} value={""} hidden={true} style={{display: "none"}}></MenuItem>
								{productCategories.map(it =>
									<MenuItem key={it.toLowerCase()}
										value={it.toUpperCase()}>{translate(it)}</MenuItem>)}
							</Select>
						</FormControl>
						<TextField
							variant={"standard"}
							label={translate("Name")}
							value={it.name ?? ""}
							style={{flexGrow: 1}}
							disabled={props.disabled}
							onChange={event => {
								const newItem = {...it, name: event.target.value};
								const newList = [...features];
								newList[index] = newItem;
								props.setSupplier({...props.supplier, features: newList});
							}}
						/>
						<TextField
							variant={"standard"}
							label={translate("Value")}
							value={it.value ?? ""}
							style={{flexGrow: 1}}
							disabled={props.disabled}
							onChange={event => {
								const newItem = {...it, value: event.target.value};
								const newList = [...features];
								newList[index] = newItem;
								props.setSupplier({...props.supplier, features: newList});
							}}
						/>
					</Stack>;
				}).filter(it => !!it)}
				
				<div>
					<IconButton
						color={"primary"}
						disabled={props.disabled}
						onClick={() => {
							const newList = [...features, {clientName: tenantId} as Feature];
							props.setSupplier({...props.supplier, features: newList});
						}}
					>
						<Add/>
					</IconButton>
				</div>
			</Stack>}
		/>
	</TabPanel>;
}

interface TaskDivisionProps extends TabProps, TenantProps, SupplierProps {
}

function TaskDivisionPanel(props: TaskDivisionProps) {
	const tenantId = props.tenant[0] ?? defaultTenantName;
	
	const taskDivisions = props.supplier.taskDivisions ?? [];
	
	function count(tenant: string) {
		return taskDivisions.filter(it => it.clientName === tenant).length;
	}
	
	return <TabPanel value={props.tab} index={4}>
		<PentaSplit
			top={<Stack spacing={2}>
				<div>{translate("Task Division")}</div>
				<FormControl>
					<InputLabel id={"tenant-label"}>{translate("Tenant")}</InputLabel>
					<Select
						labelId={"tenant-label"}
						label={"Tenant"}
						variant={"standard"}
						value={tenantId}
						onChange={it => props.tenant[1](it.target.value)}
					>
						{tenants.map(it =>
							<MenuItem key={it.toLowerCase()}
								value={it.toLowerCase()}>{translate(it)} ({count(it.toLowerCase())})</MenuItem>)}
					</Select>
				</FormControl>
			</Stack>}
			center={<Stack spacing={2} style={{height: "100%", overflow: "auto"}}>
				{taskDivisions.map((it, index) => {
					if (tenantId !== it.clientName)
						return undefined;
					return <Stack key={index} spacing={2} direction={"row"} alignItems={"end"} padding={"10px 0"}>
						<IconButton
							color={"primary"}
							disabled={props.disabled}
							onClick={() => {
								const newList = [...taskDivisions];
								newList.splice(index, 1);
								props.setSupplier({...props.supplier, taskDivisions: newList});
							}}
						>
							<Remove/>
						</IconButton>
						<FormControl>
							<InputLabel
								id={`${index}-product-category-label`}>{translate("Product Category")}</InputLabel>
							<Select
								style={{width: "200px"}}
								labelId={`${index}-product-category-label`}
								label={translate("Product Category")}
								variant={"standard"}
								value={it.productCategory ?? ""}
								disabled={props.disabled}
								onChange={event => {
									const newItem = {...it, productCategory: event.target.value};
									const newList = [...taskDivisions];
									newList[index] = newItem;
									props.setSupplier({...props.supplier, taskDivisions: newList});
								}}
							>
								<MenuItem key={"null"} value={""} hidden={true} style={{display: "none"}}></MenuItem>
								{productCategories.map(it =>
									<MenuItem key={it.toLowerCase()}
										value={it.toUpperCase()}>{translate(it)}</MenuItem>)}
							</Select>
						</FormControl>
						{/* todo make dropdown perhaps */}
						<TextField
							variant={"standard"}
							label={translate("Name")}
							value={it.name ?? ""}
							style={{width: "200px"}}
							disabled={props.disabled}
							onChange={event => {
								const newItem = {...it, name: event.target.value};
								const newList = [...taskDivisions];
								newList[index] = newItem;
								props.setSupplier({...props.supplier, taskDivisions: newList});
							}}
						/>
						<TextField
							variant={"standard"}
							label={translate("Value")}
							value={it.value ?? ""}
							style={{flexGrow: 1}}
							disabled={props.disabled}
							onChange={event => {
								const newItem = {...it, value: event.target.value};
								const newList = [...taskDivisions];
								newList[index] = newItem;
								props.setSupplier({...props.supplier, taskDivisions: newList});
							}}
						/>
					</Stack>;
				}).filter(it => !!it)}
				
				<div>
					<IconButton
						color={"primary"}
						disabled={props.disabled}
						onClick={() => {
							const newList = [...taskDivisions, {clientName: tenantId} as TaskDivision];
							props.setSupplier({...props.supplier, taskDivisions: newList});
						}}
					>
						<Add/>
					</IconButton>
				</div>
			</Stack>}
		/>
	</TabPanel>;
}

interface HowItWorksProps extends TabProps, TenantProps, SupplierProps {
}

function HowItWorksPanel(props: HowItWorksProps) {
	const tenantId = props.tenant[0] ?? defaultTenantName;
	
	const howItWorks = props.supplier.howItWorks ?? [];
	
	function count(tenant: string) {
		return howItWorks.filter(it => it.clientName === tenant).length;
	}
	
	return <TabPanel value={props.tab} index={5}>
		<PentaSplit
			top={<Stack spacing={2}>
				<div>{translate("How it works")}</div>
				<FormControl>
					<InputLabel id={"tenant-label"}>{translate("Tenant")}</InputLabel>
					<Select
						labelId={"tenant-label"}
						label={translate("Tenant")}
						variant={"standard"}
						value={tenantId}
						onChange={it => props.tenant[1](it.target.value)}
					>
						{tenants.map(it =>
							<MenuItem key={it.toLowerCase()}
								value={it.toLowerCase()}>{translate(it)} ({count(it.toLowerCase())})</MenuItem>)}
					</Select>
				</FormControl>
			</Stack>}
			center={<Stack spacing={2} style={{height: "100%", overflow: "auto"}}>
				{howItWorks.map((it, index) => {
					if (tenantId !== it.clientName)
						return undefined;
					return <Stack key={index} spacing={2} direction={"row"} alignItems={"end"} padding={"10px 0"}>
						<IconButton
							color={"primary"}
							onClick={() => {
								const newList = [...howItWorks];
								newList.splice(index, 1);
								props.setSupplier({...props.supplier, howItWorks: newList});
							}}
						>
							<Remove/>
						</IconButton>
						<FormControl>
							<InputLabel
								id={`${index}-product-category-label`}>{translate("Product Category")}</InputLabel>
							<Select
								style={{width: "200px"}}
								labelId={`${index}-product-category-label`}
								label={translate("Product Category")}
								variant={"standard"}
								value={it.productCategory ?? ""}
								onChange={event => {
									const newItem = {...it, productCategory: event.target.value};
									const newList = [...howItWorks];
									newList[index] = newItem;
									props.setSupplier({...props.supplier, howItWorks: newList});
								}}
							>
								<MenuItem key={"null"} value={""} hidden={true} style={{display: "none"}}></MenuItem>
								{productCategories.map(it =>
									<MenuItem key={it.toLowerCase()}
										value={it.toUpperCase()}>{translate(it)}</MenuItem>)}
							</Select>
						</FormControl>
						{/* todo make dropdown perhaps */}
						<TextField
							variant={"standard"}
							label={translate("Type Name")}
							value={it.typeName ?? ""}
							style={{width: "200px"}}
							onChange={event => {
								const newItem = {...it, typeName: event.target.value};
								const newList = [...howItWorks];
								newList[index] = newItem;
								props.setSupplier({...props.supplier, howItWorks: newList});
							}}
						/>
						<TextField
							variant={"standard"}
							label={translate("Value")}
							value={it.value ?? ""}
							style={{flexGrow: 1}}
							onChange={event => {
								const newItem = {...it, value: event.target.value};
								const newList = [...howItWorks];
								newList[index] = newItem;
								props.setSupplier({...props.supplier, howItWorks: newList});
							}}
						/>
					</Stack>;
				}).filter(it => !!it)}
				
				<div>
					<IconButton
						color={"primary"}
						onClick={() => {
							const newList = [...howItWorks, {clientName: tenantId} as HowItWorks];
							props.setSupplier({...props.supplier, howItWorks: newList});
						}}
					>
						<Add/>
					</IconButton>
				</div>
			</Stack>}
		/>
	</TabPanel>;
}

interface ProductsProps extends TabProps, TenantProps {
}

function ProductsPanel(props: ProductsProps) {
	return <TabPanel value={props.tab} index={6}>
		<Stack style={{height: "100%"}}>
			<FormControl>
				<InputLabel id={"tenant-label"}>{translate("Tenant")}</InputLabel>
				<Select
					labelId={"tenant-label"}
					label={translate("Tenant")}
					variant={"standard"}
					value={props.tenant[0] ?? defaultTenantName}
					onChange={it => props.tenant[1](it.target.value)}
				>
					{tenants.map(it =>
						<MenuItem key={it.toLowerCase()} value={it.toLowerCase()}>{translate(it)}</MenuItem>)}
				</Select>
			</FormControl>
			<ProductsOverviewContent tenant={props.tenant[0] ?? defaultTenantName}/>
		</Stack>
	</TabPanel>;
}

export function openProductSupplierDetailsForNew(krn: string, name: string, legalName: string) {
	const id = crypto.randomUUID();
	const supplier = {id, krn, name, legalName, state: "ACTIVE"} as ProductSupplier;
	newSupplier.set(supplier);
	openProductSupplierDetailsFor(krn);
}

export function openProductSupplierDetailsFor(krn: string) {
	window.location.href = linkProductSupplierDetailsFor(krn);
}

export function linkProductSupplierDetailsFor(krn: string): string {
	const id = krn.split(":").lastOrNull() ?? "";
	return `/products/suppliers/${encodeURIComponent(id)}`;
}
